/** QuickPlan — Sections 2D des profilés (extrusions).
 *
 * Une section = { outer: [[x,y]…], holes: [[[x,y]…]…] } en mm, centrée sur (0,0),
 * contour fermé implicitement (le dernier point rejoint le premier).
 *
 * Les profilés aluminium à rainure sont GÉNÉRÉS par série (20, 30, 40, 45) et par
 * nombre de cellules (1×1, 1×2, 2×2…) : une rainure en T au milieu de chaque côté
 * de cellule, un alésage central par cellule. Géométrie indicative (aspect et
 * encombrement justes, cotes de rainure approchées) — pas un plan constructeur.
 */

/** Séries de profilés à rainure : pas de cellule, largeur d'ouverture de rainure,
 *  épaisseur de lèvre, largeur intérieure, profondeur, Ø alésage central. */
export const SLOT_SERIES = {
  20: { cell: 20, slot: 6, lip: 1.8, inner: 11, depth: 6, bore: 4.2 },
  30: { cell: 30, slot: 8, lip: 2.2, inner: 16.5, depth: 9, bore: 6.8 },
  40: { cell: 40, slot: 8, lip: 4.3, inner: 20, depth: 12.2, bore: 8.5 },
  45: { cell: 45, slot: 10, lip: 4.5, inner: 22, depth: 13.5, bore: 10 },
};

function circle(cx, cy, r, n = 20) {
  const pts = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return pts;
}

/** Profilé à rainure : série `s`, nx cellules en largeur, ny en hauteur. */
export function slotSection(series, nx = 1, ny = 1) {
  const S = SLOT_SERIES[series];
  if (!S) throw new Error(`série de profilé inconnue : ${series}`);
  const W = S.cell * nx;
  const H = S.cell * ny;
  const x0 = -W / 2;
  const y0 = -H / 2;
  const outer = [];
  // Demi-largeur du fond de rainure, bornée pour garder une âme de matière dans l'angle :
  // au-delà, les chambres de deux côtés voisins se croisent et le contour se recoupe.
  const floor = Math.min(S.inner / 2 - (S.inner - S.slot) / 4, S.cell / 2 - S.depth - 0.5);

  // Un côté : de `a` vers `b` (sens trigonométrique), normale intérieure `n`,
  // une rainure au milieu de chaque cellule.
  const side = (a, b, n, cells) => {
    const dx = (b[0] - a[0]) / cells;
    const dy = (b[1] - a[1]) / cells;
    const len = Math.hypot(dx, dy);
    const t = [dx / len, dy / len];
    outer.push(a);
    for (let k = 0; k < cells; k++) {
      const m = [a[0] + dx * (k + 0.5), a[1] + dy * (k + 0.5)];
      const at = (along, inward) => [m[0] + t[0] * along + n[0] * inward, m[1] + t[1] * along + n[1] * inward];
      outer.push(
        at(-S.slot / 2, 0),
        at(-S.slot / 2, S.lip),
        at(-S.inner / 2, S.lip),
        at(-floor, S.depth),
        at(floor, S.depth),
        at(S.inner / 2, S.lip),
        at(S.slot / 2, S.lip),
        at(S.slot / 2, 0),
      );
    }
  };
  side([x0, y0], [x0 + W, y0], [0, 1], nx); // bas
  side([x0 + W, y0], [x0 + W, y0 + H], [-1, 0], ny); // droite
  side([x0 + W, y0 + H], [x0, y0 + H], [0, -1], nx); // haut
  side([x0, y0 + H], [x0, y0], [1, 0], ny); // gauche

  const holes = [];
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      holes.push(circle(x0 + S.cell * (i + 0.5), y0 + S.cell * (j + 0.5), S.bore / 2));
    }
  }
  return { outer, holes };
}

/** Tube rectangulaire w×h, paroi e. */
export function tubeSection(w, h, e) {
  const rect = (a, b) => [[-a / 2, -b / 2], [a / 2, -b / 2], [a / 2, b / 2], [-a / 2, b / 2]];
  return { outer: rect(w, h), holes: e > 0 && e * 2 < Math.min(w, h) ? [rect(w - 2 * e, h - 2 * e).reverse()] : [] };
}

/** Cornière à ailes égales a, épaisseur e (angle en bas à gauche). */
export function angleSection(a, e) {
  const h = a / 2;
  return { outer: [[-h, -h], [h, -h], [h, -h + e], [-h + e, -h + e], [-h + e, h], [-h, h]], holes: [] };
}

/** Résout une référence de section : objet {outer, holes} ou identifiant texte
 *  « slot40-1x2 », « tube40x20x2 », « angle30x3 ». */
export function resolveSection(ref) {
  if (ref && typeof ref === 'object') return { outer: ref.outer, holes: ref.holes ?? [] };
  const s = String(ref);
  let m = s.match(/^slot(\d+)-(\d+)x(\d+)$/);
  if (m) return slotSection(Number(m[1]), Number(m[2]), Number(m[3]));
  m = s.match(/^tube([\d.]+)x([\d.]+)x([\d.]+)$/);
  if (m) return tubeSection(Number(m[1]), Number(m[2]), Number(m[3]));
  m = s.match(/^angle([\d.]+)x([\d.]+)$/);
  if (m) return angleSection(Number(m[1]), Number(m[2]));
  throw new Error(`section inconnue : ${s}`);
}

/** Boîte englobante d'une section : { minX, maxX, minY, maxY }. */
export function sectionBounds(sec) {
  const xs = sec.outer.map((p) => p[0]);
  const ys = sec.outer.map((p) => p[1]);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
}

/** Aire signée (shoelace) — positive si sens trigonométrique. */
export function polygonArea(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    a += x1 * y2 - x2 * y1;
  }
  return a / 2;
}

/** Aire nette de matière (mm²) : contour − alésages. Sert au poids linéique. */
export function sectionArea(sec) {
  return Math.abs(polygonArea(sec.outer)) - sec.holes.reduce((s, h) => s + Math.abs(polygonArea(h)), 0);
}
